import { History, KeyRound, Lock, MoreHorizontal, Unlock } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "@/components/ui/use-toast";
import { useConfirmationAlert } from "@/hook/confirmation-alert/hooks/actions";
import { useDialogModal } from "@/hook/handle-modal/hooks/actions";
import {
  IStoreUserRecord,
  useLazyGetAuditHistoryQuery,
  useResetUserPasswordMutation,
  useToggleUserBlockMutation,
} from "@/services/store-access-query";

import { extractErrorMessage } from "../../hooks/utils";
import { AuditHistoryContent } from "../row-actions/audit-history-content";
import { USERS_TAB_MESSAGES } from "./messages";

interface UserRowActionsProps {
  record: IStoreUserRecord;
  storeCode: number;
}

type UserAction = "block" | "unblock" | "resetPassword";

/**
 * Diferenças vs `OperatorRowActions`:
 *   - o reset de senha grava em RSP001 (texto puro), não em W07USU.
 *   - `entityId` do audit é `${storeCode}-${userCode}` (userCode numérico).
 */
export const UserRowActions: React.FC<UserRowActionsProps> = ({
  record,
  storeCode,
}) => {
  const [toggleUserBlock, { isLoading: isToggling }] =
    useToggleUserBlockMutation();
  const [resetUserPassword, { isLoading: isResetting }] =
    useResetUserPasswordMutation();
  const [getAuditHistory] = useLazyGetAuditHistoryQuery();
  const { handleAlert } = useConfirmationAlert();
  const { handleModal } = useDialogModal();

  const isLoading = isToggling || isResetting;

  const getModalConfig = (action: UserAction) => {
    const name = record.userName || String(record.userCode);
    const configByAction = {
      block: USERS_TAB_MESSAGES.BLOCK.MODAL,
      unblock: USERS_TAB_MESSAGES.UNBLOCK.MODAL,
      resetPassword: USERS_TAB_MESSAGES.RESET_PASSWORD.MODAL,
    };
    const config = configByAction[action];
    return {
      title: config.title,
      description: config.description(name),
      confirmText: config.confirmText,
    };
  };

  const executeAction = async (action: UserAction) => {
    if (action === "resetPassword") {
      const result = await resetUserPassword({
        storeCode,
        userCode: record.userCode,
      }).unwrap();
      toast({
        ...USERS_TAB_MESSAGES.RESET_PASSWORD.SUCCESS,
        description:
          result.message || USERS_TAB_MESSAGES.RESET_PASSWORD.SUCCESS.description,
      });
      return;
    }

    const blocked = action === "block";
    await toggleUserBlock({
      storeCode,
      userCode: record.userCode,
      blocked,
    }).unwrap();
    toast(
      blocked
        ? USERS_TAB_MESSAGES.BLOCK.SUCCESS
        : USERS_TAB_MESSAGES.UNBLOCK.SUCCESS,
    );
  };

  const handleAction = (action: UserAction) => {
    const config = getModalConfig(action);
    handleAlert({
      isOpen: true,
      title: config.title,
      description: config.description,
      confirmText: config.confirmText,
      isLoading: false,
      onConfirm: async () => {
        try {
          await executeAction(action);
        } catch (error) {
          const fallback =
            action === "resetPassword"
              ? USERS_TAB_MESSAGES.RESET_PASSWORD.ERROR
              : USERS_TAB_MESSAGES.TOGGLE.ERROR;
          toast({
            ...fallback,
            description: extractErrorMessage(error) || fallback.description,
          });
          console.error("Erro na ação de usuário:", error);
          throw error;
        }
      },
    });
  };

  const handleViewHistory = async () => {
    const entityId = `${storeCode}-${record.userCode}`;
    try {
      const result = await getAuditHistory([entityId]).unwrap();
      handleModal({
        isOpen: true,
        title: "Histórico de Ações",
        description: `Histórico de ações do usuário: ${record.userName || record.userCode}`,
        element: <AuditHistoryContent data={result} />,
      });
    } catch (error) {
      toast({
        ...USERS_TAB_MESSAGES.AUDIT_HISTORY.ERROR,
        description:
          extractErrorMessage(error) ||
          USERS_TAB_MESSAGES.AUDIT_HISTORY.ERROR.description,
      });
      console.error("Erro ao buscar histórico:", error);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0"
          aria-label="Abrir ações do usuário"
        >
          <span className="sr-only">Abrir menu</span>
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {record.blocked ? (
          <DropdownMenuItem
            onClick={() => handleAction("unblock")}
            disabled={isLoading}
          >
            <Unlock className="mr-2 h-4 w-4" />
            Desbloquear Usuário
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem
            onClick={() => handleAction("block")}
            disabled={isLoading}
          >
            <Lock className="mr-2 h-4 w-4" />
            Bloquear Usuário
          </DropdownMenuItem>
        )}
        <DropdownMenuItem
          onClick={() => handleAction("resetPassword")}
          disabled={isLoading}
        >
          <KeyRound className="mr-2 h-4 w-4" />
          Resetar Senha
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleViewHistory}>
          <History className="mr-2 h-4 w-4" />
          Ver Histórico
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
